import _assign from "object-assign";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MuiButton from '@material-ui/core/Button';
import { refType } from '@material-ui/utils';
import classnames from 'classnames';
import { codes } from 'keycode';
import { DensityContext, withStyles } from '@jpmuitk/theme'; // Style injection is based on source order, see:
// https://github.com/mui-org/material-ui/blob/89ebedc24f97d6bc7ca2e34a00efcdd47ca16812/packages/material-ui-styles/src/makeStyles/indexCounter.js#L5-L13
// This implicit child relationship is not known when Icons are children.

import "@jpmuitk/icon/es/Icon";
import { IconClassNameProvider } from "@jpmuitk/icon/es/internal/IconClassNameContext";
import { buttonStyle } from './style';

/**
 * Button component, provides the user a simple way to trigger an event. It supports text and/or an icon.
 */
export var Button = /*#__PURE__*/React.forwardRef(function Button(props, ref) {
  var contextDensity = useContext(DensityContext);

  var _props$disableFocusRi = props.disableFocusRipple,
      disableFocusRipple = _props$disableFocusRi === void 0 ? true : _props$disableFocusRi,
      _props$disableRipple = props.disableRipple,
      disableRipple = _props$disableRipple === void 0 ? true : _props$disableRipple,
      _props$focusRipple = props.focusRipple,
      focusRipple = _props$focusRipple === void 0 ? false : _props$focusRipple,
      children = props.children,
      classes = props.classes,
      className = props.className,
      _props$density = props.density,
      density = _props$density === void 0 ? contextDensity : _props$density,
      hasInputParent = props.hasInputParent,
      onBlur = props.onBlur,
      onClick = props.onClick,
      onKeyDown = props.onKeyDown,
      onKeyUp = props.onKeyUp,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'regular' : _props$variant,
      rest = _objectWithoutProperties(props, ["disableFocusRipple", "disableRipple", "focusRipple", "children", "classes", "className", "density", "hasInputParent", "onBlur", "onClick", "onKeyDown", "onKeyUp", "variant"]);

  var _useState = useState(false),
      active = _useState[0],
      setActive = _useState[1];

  var _useState2 = useState(0),
      keyIsDown = _useState2[0],
      setkeyIsDown = _useState2[1];

  useEffect(function () {
    var t = setTimeout(function () {
      // This key state check is to stop continual visual state change when using Enter Key, which the browser treats as both key and click events on a Button
      if (keyIsDown !== codes.enter) {
        setActive(false);
      }
    }, 0);
    return function () {
      clearTimeout(t);
    };
  }, [active, keyIsDown]);

  var handleKeyDown = function handleKeyDown(event) {
    if ([codes.enter, codes.space].indexOf(event.which) >= 0) {
      setkeyIsDown(event.which);
      setActive(true);
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  var handleKeyUp = function handleKeyUp(event) {
    setkeyIsDown(0);
    setActive(false);

    if (onKeyUp) {
      onKeyUp(event);
    }
  };

  var handleClick = function handleClick(event) {
    setActive(true);

    if (onClick) {
      onClick(event);
    }
  };

  var handleBlur = function handleBlur(event) {
    setActive(false);

    if (onBlur) {
      onBlur(event);
    }
  };

  if (!classes) {
    return null;
  }

  return /*#__PURE__*/React.createElement(MuiButton, _assign({
    className: classnames(className, classes["".concat(density, "Density")], {
      active: active
    }, classes[variant], _defineProperty({}, classes.inputButton, hasInputParent)),
    classes: {
      disabled: classes.disabled,
      root: classes.root,
      label: classes.label,
      focusVisible: classes.focusVisible
    },
    disableFocusRipple: disableFocusRipple,
    disableRipple: disableRipple,
    focusRipple: focusRipple,
    focusVisibleClassName: classes.focusVisible,
    onBlur: handleBlur,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    ref: ref
  }, rest), /*#__PURE__*/React.createElement(IconClassNameProvider, {
    value: classes.icon
  }, children));
});
process.env.NODE_ENV !== "production" ? Button.propTypes = {
  /**
   * @external - material-ui
   * Pass a ref callback to the native button component.
   */
  buttonRef: refType,

  /**
   * The content of the component.
   */
  children: PropTypes.node,

  /**
   * The className(s) of the component
   */
  className: PropTypes.string,

  /**
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * The density of a component affects the style of the layout.
   * A high density component uses minimal sizing and spacing to convey the intended UI design.
   * Conversely, a low density component, maximizes the use of space and size to convey the UI Design.
   */
  density: PropTypes.oneOf(['touch', 'low', 'medium', 'high']),

  /**
   * @external - material-ui
   * If `true`, the button will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * @ignore - this is used by `custom element` only.
   */
  hasInputParent: PropTypes.bool,

  /**
   * @external - material-ui
   * The URL to link to when the button is clicked. If defined, an `a` element will be used as the root node.
   */
  href: PropTypes.string,

  /**
   * @external - material-ui
   * Callback to the blur event
   */
  onBlur: PropTypes.func,

  /**
   * @external - material-ui
   * Callback to the click event
   */
  onClick: PropTypes.func,

  /**
   * @external - material-ui
   * Callback to the Keyboard down event
   */
  onKeyDown: PropTypes.func,

  /**
   * @external - material-ui
   * Callback to the Keyboard up event
   */
  onKeyUp: PropTypes.func,

  /**
   * @external - material-ui
   * Native <button /> element type.
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  type: PropTypes.string,

  /**
   * Determines the variant of the component. Must be one of: 'cta', 'secondary'.
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  variant: PropTypes.oneOf(['cta', 'regular', 'secondary']).isRequired
} : void 0;
Button.defaultProps = {
  disableFocusRipple: true,
  disableRipple: true,
  focusRipple: false,
  variant: 'regular'
}; // TODO (BREAKING CHANGE REQUIRED)
// https://issuetracking.jpmchase.net/jira12/browse/JPMUITK-2835
// Add unique JPMUI prefix to name to identify component as a Toolkit component

export default withStyles(buttonStyle, {
  name: 'Button'
})(Button);
//# sourceMappingURL=Button.js.map